// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@stanion/App/Components/Common/XngZone";
import { xScreen } from "@stanion/XDL/Utilities";
import * as React from "react";
import { css } from "styled-components";

export interface HotSheetPageStyles {
    pageStyle?: InjectableCss;
    headerSectionWrapper?: InjectableCss;
    title?: InjectableCss;
    actions?: InjectableCss;
    tabels?: InjectableCss;
}

export const hotSheetPageStyles: HotSheetPageStyles = {
    headerSectionWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            column-gap: 20px;
            > div {
                flex: 1;
            }
            ${xScreen("lt", "sm")} {
                flex-direction: column;
            }
        `,
    },
    tabels: {
        css: css`
            > div {
                display: flex;
                flex-wrap: wrap;
                column-gap: 20px;
                row-gap: 40px;
                > div {
                    flex: 0 1 calc(100% / 4 - 20px);
                    ${xScreen("lte", "md")} {
                        flex: 0 1 calc(100% / 2 - 20px);
                    }
                    ${xScreen("lt", "sm")} {
                        flex: 1 1 100%;
                    }
                }

                @media print {
                    flex-direction: column;
                }
            }
        `,
    },
};

type Props = PageProps;

const styles = hotSheetPageStyles;

export interface ExcelHotSheetView {
    [key: string]: { title: string; products: Array<{ erpNumber?: string; price?: string }> };
}

type HotSheetContextType = {
    views: ExcelHotSheetView;
    setViews: React.Dispatch<React.SetStateAction<ExcelHotSheetView>>;
};

export const HotSheetContext = React.createContext<HotSheetContextType>({
    views: {},
    setViews: () => [],
});

const HotSheetPage: React.FunctionComponent<Props> = ({ id }: Props) => {
    const [views, setViews] = React.useState<ExcelHotSheetView>({});

    React.useEffect(() => {
        setViews({});
    }, [id]);

    return (
        <Page data-test-selector="HotSheetPage">
            <StyledWrapper id={id} {...styles.pageStyle} className="hot-sheet-page">
                <HotSheetContext.Provider value={{ views, setViews }}>
                    <StyledWrapper {...styles.headerSectionWrapper}>
                        <StyledWrapper {...styles.title}>
                            <XngZone contentId={id} hint="Title" zoneName="content01" />
                        </StyledWrapper>
                        <StyledWrapper {...styles.actions} className="actions">
                            <XngZone contentId={id} hint="Actions" zoneName="content02" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.tabels} className="tables">
                        <XngZone contentId={id} hint="Hot Sheet Tabels" zoneName="content03" />
                    </StyledWrapper>
                </HotSheetContext.Provider>
            </StyledWrapper>
        </Page>
    );
};

const pageModule: PageModule = {
    component: HotSheetPage,
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
    },
};

export const HotSheetPageContext = "HotSheetPage";

export default pageModule;
